// Utils and Service
import { getErrorMessage, isCanceledError } from '../utils/helpers'

// eslint-disable-next-line import/no-cycle
import axios from './instance'

/**
 * Shared GET for the read-only analytics endpoints. Returns the (camelCased)
 * response body, or null when the request was superseded/cancelled.
 */
const getAnalytics = async (
  url,
  setLoading = () => {},
  setError = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const data = await axios.get(url, { signal })
    setLoading(false)
    return data
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

const withQuery = (path, query) => (query ? `${path}?${query}` : path)

/** Cross-dealer KPI snapshot (same shape as the dealer KPIs). */
export const getAnalyticsKpis = (query, setLoading, setError, signal) =>
  getAnalytics(withQuery('/analytics/kpis/', query), setLoading, setError, signal)

/** Bucketed Performance Explorer time series. */
export const getAnalyticsTimeseries = (query, setLoading, setError, signal) =>
  getAnalytics(withQuery('/analytics/timeseries/', query), setLoading, setError, signal)

/** Distinct advisor names in scope (for the Service Advisor filter). */
export const getAnalyticsAdvisors = (query, setLoading, setError, signal) =>
  getAnalytics(withQuery('/analytics/advisors/', query), setLoading, setError, signal)

/** Distinct technician names in scope (for the Technician filter). */
export const getAnalyticsTechnicians = (query, setLoading, setError, signal) =>
  getAnalytics(withQuery('/analytics/technicians/', query), setLoading, setError, signal)

/**
 * Cross-dealer RO list behind the Analytics flag (same payload as the global RO
 * list). Powers the Detailed Data drill-down. Returns { results, count, ... }.
 */
export const getAnalyticsRepairOrders = (query, setLoading, setError, signal) =>
  getAnalytics(withQuery('/analytics/repair-orders/', query), setLoading, setError, signal)

/**
 * Fetch a specific page of the drill-down list by its full URL (the `next` link
 * from a prior response) — used for "Load more".
 */
export const getAnalyticsRepairOrdersPage = (url, setLoading, setError, signal) =>
  getAnalytics(url, setLoading, setError, signal)

/**
 * Kick off an async .xlsx export of the current explorer view. The explorer
 * params + filters travel as the query string (captured server-side).
 */
export const createAnalyticsExport = async (query, setError = () => {}) => {
  try {
    return await axios.post(withQuery('/analytics/exports/', query))
  } catch (err) {
    setError(getErrorMessage(err))
    return null
  }
}

/** Poll an export's status; once EXPORTED, the response carries the file URL. */
export const getAnalyticsExport = async (id, setError = () => {}, signal = undefined) => {
  try {
    return await axios.get(`/analytics/exports/${id}/`, { signal })
  } catch (err) {
    // Superseded request — stay quiet; the newer poll owns the state.
    if (isCanceledError(err)) return null
    setError(getErrorMessage(err))
    return null
  }
}
